import { EmblaCarouselType } from 'embla-carousel'
import { useCallback, useEffect, useRef, useState } from 'react'

type UseAutoplayProgressType = {
  showAutoplayProgress: boolean
}

export const useAutoplayProgress = <ProgressElement extends HTMLElement | null>(
  emblaApi: EmblaCarouselType | undefined,
  progressNode: React.RefObject<ProgressElement>
): UseAutoplayProgressType => {
  const [showAutoplayProgress, setShowAutoplayProgress] = useState(true)
  const animationName = useRef('autoplay-progress')
  const timeoutId = useRef(0)
  const rafId = useRef(0)

  const startProgress = useCallback(
    (timeUntilNext: number | null, delay: number) => {
      const node = progressNode.current

      if (!node) {
        return
      }
      if (timeUntilNext === null) {
        return
      }

      if (!animationName.current) {
        const style = window.getComputedStyle(node)
        animationName.current = style.animationName
      }

      // eslint-disable-next-line
      node.style.animationName = 'none'
      node.style.transform = 'translate3d(0,0,0)'

      rafId.current = window.requestAnimationFrame(() => {
        timeoutId.current = window.setTimeout(() => {
          node.style.animationName = animationName.current
          node.style.animationDuration = `${timeUntilNext + delay}ms`
        }, 0)
      })

      setShowAutoplayProgress(true)
    },
    [progressNode]
  )

  useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    const delay = autoplay?.options?.delay ?? 0
    if (!autoplay || !delay) {
      return
    }

    emblaApi
      .on('autoplay:timerset', () => {
        startProgress(autoplay?.timeUntilNext(), delay as number)
      })
      .on('autoplay:timerstopped', () => {
        setShowAutoplayProgress(false)
      })
  }, [emblaApi])

  useEffect(
    () => () => {
      cancelAnimationFrame(rafId.current)
      clearTimeout(timeoutId.current)
    },
    []
  )

  return {
    showAutoplayProgress
  }
}
